/**
 * PDF text-token unit detection — ported from the Claude Design prototype.
 *
 * Extracts text items + positions from a vector PDF's first page, then filters
 * tokens that look like unit codes (configurable regex). Each surviving token
 * becomes a unit rectangle centred on the label.
 *
 * Use case: a leasing plan PDF where every unit is labelled "A-101", "F2-12B",
 * etc. — one upload yields ~100 pre-traced units the admin can review.
 */
import * as pdfjsLib from 'pdfjs-dist/build/pdf';

export const DEFAULT_CODE_PATTERN = '^[A-Za-z]{0,4}[-/. ]?\\d{1,4}[A-Za-z]?([-/]\\d{1,4}[A-Za-z]?)*$';

const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

function buildRegex(pattern) {
    try { return new RegExp(pattern, 'i'); }
    catch { return new RegExp(DEFAULT_CODE_PATTERN, 'i'); }
}

/**
 * Extract text tokens (with normalized positions) from page 1 of a PDF.
 * Returns [{ s, nx, ny, h }] normalized to the page, or [] if the PDF has no
 * text layer (flattened / scanned PDFs fall through to OCR).
 */
export async function extractPdfTextTokens(src) {
    const pdf = await pdfjsLib.getDocument(src).promise;
    const page = await pdf.getPage(1);
    const vp = page.getViewport({ scale: 1 });
    let tc;
    try { tc = await page.getTextContent(); }
    catch { return []; }

    return tc.items
        .map((it) => {
            const m = pdfjsLib.Util.transform(vp.transform, it.transform);
            return {
                s: String(it.str || '').trim(),
                nx: m[4] / vp.width,
                ny: m[5] / vp.height,
                h: Math.hypot(m[2], m[3]) / vp.height,
            };
        })
        .filter((t) => t.s);
}

/**
 * Tokens → unit rectangles. First occurrence of each code wins; rectangles are
 * a fixed default size (8.5% × 6.5% of the plan) centred on the label.
 */
export function detectRectsFromTokens(tokens, pattern = DEFAULT_CODE_PATTERN) {
    const re = buildRegex(pattern);
    const seen = new Set();
    const out = [];
    let i = 0;
    for (const t of tokens) {
        const s = String(t.s || '').replace(/\s+/g, '');
        if (s.length < 2 || s.length > 12) continue;
        if (!/\d/.test(s)) continue;
        if (!re.test(s)) continue;
        const key = s.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);

        const w = 0.085, h = 0.065;
        out.push({
            id: 'd' + Date.now().toString(36) + (i++),
            code: s,
            detected: true,
            nx: clamp(t.nx - w / 2, 0, 1 - w),
            ny: clamp(t.ny - h / 2, 0, 1 - h),
            nw: w,
            nh: h,
        });
    }
    return out;
}
